import { useCallback, useEffect, useState } from 'react';
import { useT } from '../context/LocaleContext';
import { resolveMediaObject } from '../lib/bookingMedia';
import { supabase } from '../lib/supabase';

const CATEGORIES = ['oil', 'battery', 'tire', 'spare', 'other'];
const EVENT_TYPES = ['replace', 'rotate', 'top_up', 'inspect'];
const POSITIONS = ['FL', 'FR', 'RL', 'RR', 'all'];

const TRACKING_CATEGORIES = new Set(['spare', 'battery', 'other']);

const blankDraft = {
  category: 'oil',
  name: '',
  oil_spec: '',
  quantity: '',
  unit: 'L',
  event_type: 'replace',
  tire_position: 'all',
  part_number: '',
  manufacturer: '',
  country_of_origin: '',
  supplier: '',
  warranty_months: '',
  expected_lifespan: '',
  qr_code: '',
  photo_path: '',
  photo_name: '',
  install_date: new Date().toISOString().slice(0, 10),
  notes: '',
};

function partLabel(row) {
  if (row.category === 'oil') {
    const bits = [row.oil_spec || row.name || 'Oil', row.quantity != null ? `${row.quantity}${row.unit || ''}` : null]
      .filter(Boolean);
    return bits.join(' · ');
  }
  if (row.category === 'tire') {
    return [row.event_type, row.tire_position, row.name].filter(Boolean).join(' · ') || 'Tire work';
  }
  if (row.category === 'battery') {
    return row.name || 'Battery';
  }
  return row.name || row.part_number || 'Part';
}

function trackingSummary(row) {
  return [
    row.manufacturer,
    row.part_number ? `#${row.part_number}` : null,
    row.country_of_origin,
    row.supplier ? `via ${row.supplier}` : null,
    row.qr_code ? `S/N ${row.qr_code}` : null,
    row.expected_lifespan ? `life ${row.expected_lifespan}` : null,
  ].filter(Boolean);
}

function WorkLogPhotoThumb({ path }) {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    let revoke = false;
    async function load() {
      if (!path) {
        setUrl('');
        return;
      }
      const result = await resolveMediaObject({ bucket: 'vehicle-photos', path });
      if (!alive) {
        if (result.revoke && result.url) URL.revokeObjectURL(result.url);
        return;
      }
      revoke = result.revoke;
      if (result.error || !result.url) {
        setError('Photo');
        setUrl('');
        return;
      }
      setUrl(result.url);
      setError('');
    }
    load();
    return () => {
      alive = false;
      if (revoke && url) URL.revokeObjectURL(url);
    };
  }, [path]);

  if (!path) return null;
  if (error || !url) {
    return <span className="work-log-photo-fallback">{error || '…'}</span>;
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className="work-log-photo-thumb">
      <img src={url} alt="Part package" />
    </a>
  );
}

export default function WorkLogPanel({ booking, onPartsChanged }) {
  const t = useT();
  const [parts, setParts] = useState([]);
  const [jobCardId, setJobCardId] = useState('');
  const [draft, setDraft] = useState(blankDraft);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  const showTracking = TRACKING_CATEGORIES.has(draft.category);
  const vehicleId = booking?.vehicle_id || booking?.vehicles?.id;

  const load = useCallback(async () => {
    if (!booking?.id) return;
    setLoading(true);
    setError('');
    const { data: cardId, error: cardError } = await supabase.rpc(
      'ensure_job_card_for_booking_id',
      { p_booking_id: booking.id },
    );
    if (cardError) {
      setError(cardError.message);
      setLoading(false);
      return;
    }
    setJobCardId(cardId);
    const { data, error: partsError } = await supabase
      .from('job_card_parts')
      .select('*')
      .eq('job_card_id', cardId)
      .order('created_at', { ascending: false });
    if (partsError) {
      setError(partsError.message);
      setLoading(false);
      return;
    }
    setParts(data || []);
    setLoading(false);
  }, [booking?.id]);

  useEffect(() => {
    load();
  }, [load]);

  async function uploadPackagePhoto(file) {
    if (!file || !vehicleId) return;
    setUploading(true);
    setError('');
    const { data: authData, error: authError } = await supabase.auth.getUser();
    if (authError || !authData?.user?.id) {
      setUploading(false);
      setError(authError?.message || 'Sign in again to upload photos.');
      return;
    }
    const ext = file.name.split('.').pop() || 'jpg';
    const path = `${authData.user.id}/parts/${vehicleId}/${crypto.randomUUID()}.${ext}`;
    const { error: uploadError } = await supabase.storage
      .from('vehicle-photos')
      .upload(path, file, { upsert: false });
    setUploading(false);
    if (uploadError) {
      setError(uploadError.message);
      return;
    }
    setDraft((prev) => ({
      ...prev,
      photo_path: path,
      photo_name: file.name,
    }));
  }

  function clearPackagePhoto() {
    setDraft((prev) => ({ ...prev, photo_path: '', photo_name: '' }));
  }

  async function save(event) {
    event.preventDefault();
    if (!jobCardId) return;
    setSaving(true);
    setError('');
    setOk('');
    const category = draft.category;
    const name =
      draft.name.trim() ||
      (category === 'oil'
        ? draft.oil_spec.trim() || 'Engine oil'
        : category === 'battery'
          ? 'Battery'
          : category === 'tire'
            ? `Tire ${draft.event_type}`
            : 'Part');
    const payload = {
      job_card_id: jobCardId,
      vehicle_id: vehicleId || null,
      category,
      name,
      oil_spec: category === 'oil' ? draft.oil_spec.trim() || null : null,
      quantity: draft.quantity === '' ? null : Number(draft.quantity),
      unit: category === 'oil' ? draft.unit || 'L' : draft.unit || null,
      event_type: category === 'tire' || category === 'oil' ? draft.event_type : null,
      tire_position: category === 'tire' ? draft.tire_position : null,
      part_number: draft.part_number.trim() || null,
      manufacturer: draft.manufacturer.trim() || null,
      country_of_origin: draft.country_of_origin.trim() || null,
      supplier: draft.supplier.trim() || null,
      warranty_months:
        draft.warranty_months === '' ? null : Number(draft.warranty_months),
      expected_lifespan: draft.expected_lifespan.trim() || null,
      qr_code: draft.qr_code.trim() || null,
      photo_url: draft.photo_path || null,
      install_date: draft.install_date || null,
      notes: draft.notes.trim() || null,
    };
    const { error: insertError } = await supabase.from('job_card_parts').insert(payload);
    setSaving(false);
    if (insertError) {
      setError(insertError.message);
      return;
    }
    setDraft({ ...blankDraft, category: draft.category });
    setOk('Added to work log.');
    onPartsChanged?.();
    await load();
  }

  async function remove(id) {
    setError('');
    const { error: deleteError } = await supabase
      .from('job_card_parts')
      .delete()
      .eq('id', id);
    if (deleteError) {
      setError(deleteError.message);
      return;
    }
    setParts((current) => current.filter((row) => row.id !== id));
    onPartsChanged?.();
  }

  if (loading) return <p className="empty">{t('worklog.opening')}</p>;

  return (
    <div className="work-log-sheet">
      <header className="quote-head">
        <div>
          <span className="kicker">{t('worklog.kicker')}</span>
          <h2>{t('worklog.title')}</h2>
          <p className="lede" style={{ marginTop: 6, maxWidth: '48ch' }}>
            {t('worklog.lede')}
          </p>
        </div>
      </header>

      {error ? <p className="form-error">{error}</p> : null}
      {ok ? <p className="form-ok">{ok}</p> : null}

      <form className="work-log-form sheet-form" onSubmit={save}>
        <div className="form-row">
          <label>
            {t('worklog.category')}
            <select
              value={draft.category}
              onChange={(e) => setDraft({ ...draft, category: e.target.value })}
            >
              {CATEGORIES.map((id) => (
                <option key={id} value={id}>
                  {t(`worklog.cat.${id}`)}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t('worklog.date')}
            <input
              type="date"
              value={draft.install_date}
              onChange={(e) => setDraft({ ...draft, install_date: e.target.value })}
            />
          </label>
        </div>

        {draft.category === 'oil' ? (
          <div className="form-row">
            <label>
              {t('worklog.oilSpec')}
              <input
                placeholder="e.g. 5W-30 LL-01"
                value={draft.oil_spec}
                onChange={(e) => setDraft({ ...draft, oil_spec: e.target.value })}
                required
              />
            </label>
            <label>
              {t('worklog.quantity')}
              <input
                type="number"
                min="0"
                step="0.1"
                value={draft.quantity}
                onChange={(e) => setDraft({ ...draft, quantity: e.target.value })}
                required
              />
            </label>
            <label>
              {t('worklog.unit')}
              <select
                value={draft.unit}
                onChange={(e) => setDraft({ ...draft, unit: e.target.value })}
              >
                <option value="L">{t('worklog.liters')}</option>
                <option value="qt">{t('worklog.quarts')}</option>
              </select>
            </label>
          </div>
        ) : null}

        {draft.category === 'tire' ? (
          <div className="form-row">
            <label>
              {t('worklog.event')}
              <select
                value={draft.event_type}
                onChange={(e) => setDraft({ ...draft, event_type: e.target.value })}
              >
                {EVENT_TYPES.map((id) => (
                  <option key={id} value={id}>
                    {t(`worklog.event.${id}`)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {t('worklog.position')}
              <select
                value={draft.tire_position}
                onChange={(e) => setDraft({ ...draft, tire_position: e.target.value })}
              >
                {POSITIONS.map((id) => (
                  <option key={id} value={id}>
                    {t(`worklog.pos.${id}`)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Size / brand
              <input
                placeholder="e.g. 225/45 R17"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </label>
          </div>
        ) : null}

        {showTracking ? (
          <>
            <label>
              Part name
              <input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                required={draft.category !== 'other'}
                placeholder={
                  draft.category === 'battery' ? 'Battery model' : 'e.g. Brake pads — front'
                }
              />
            </label>

            <fieldset className="work-log-tracking">
              <legend>Part verification (optional)</legend>
              <p className="work-log-tracking-lede">
                Shown on the customer&apos;s vehicle passport so they can verify the part
                that was installed.
              </p>
              <div className="form-row">
                <label>
                  Manufacturer
                  <input
                    value={draft.manufacturer}
                    onChange={(e) => setDraft({ ...draft, manufacturer: e.target.value })}
                    placeholder="e.g. Bosch"
                  />
                </label>
                <label>
                  Country of origin
                  <input
                    value={draft.country_of_origin}
                    onChange={(e) =>
                      setDraft({ ...draft, country_of_origin: e.target.value })
                    }
                    placeholder="e.g. Germany"
                  />
                </label>
                <label>
                  Part number
                  <input
                    value={draft.part_number}
                    onChange={(e) => setDraft({ ...draft, part_number: e.target.value })}
                    placeholder="OEM or aftermarket code"
                  />
                </label>
              </div>
              <div className="form-row">
                <label>
                  Supplier
                  <input
                    value={draft.supplier}
                    onChange={(e) => setDraft({ ...draft, supplier: e.target.value })}
                    placeholder="Who supplied the part"
                  />
                </label>
                <label>
                  Warranty (months)
                  <input
                    type="number"
                    min="0"
                    value={draft.warranty_months}
                    onChange={(e) =>
                      setDraft({ ...draft, warranty_months: e.target.value })
                    }
                  />
                </label>
                <label>
                  Expected lifespan
                  <input
                    value={draft.expected_lifespan}
                    onChange={(e) =>
                      setDraft({ ...draft, expected_lifespan: e.target.value })
                    }
                    placeholder="e.g. 50,000 km or 24 months"
                  />
                </label>
              </div>
              <label>
                QR code / serial number
                <input
                  value={draft.qr_code}
                  onChange={(e) => setDraft({ ...draft, qr_code: e.target.value })}
                  placeholder="Scanned or printed on the package"
                />
              </label>
              <div className="work-log-photo-field">
                <span className="work-log-photo-label">Package photograph</span>
                {draft.photo_path ? (
                  <div className="work-log-photo-picked">
                    <WorkLogPhotoThumb path={draft.photo_path} />
                    <div>
                      <strong>{draft.photo_name || 'Photo attached'}</strong>
                      <button type="button" className="ghost" onClick={clearPackagePhoto}>
                        Remove photo
                      </button>
                    </div>
                  </div>
                ) : (
                  <label className="work-log-photo-upload">
                    <input
                      type="file"
                      accept="image/*"
                      disabled={uploading || !vehicleId}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) uploadPackagePhoto(file);
                        e.target.value = '';
                      }}
                    />
                    {uploading ? 'Uploading…' : 'Add package photo'}
                  </label>
                )}
              </div>
            </fieldset>
          </>
        ) : null}

        <label>
          Mechanic notes
          <textarea
            rows={2}
            value={draft.notes}
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            placeholder="Optional notes for the passport"
          />
        </label>

        <div className="row-actions">
          <button type="submit" className="primary" disabled={saving || uploading}>
            {saving ? t('common.saving') : t('worklog.add')}
          </button>
        </div>
      </form>

      <div className="work-log-list">
        <h3>This visit</h3>
        {parts.length === 0 ? (
          <p className="empty">Nothing logged yet.</p>
        ) : (
          <ul className="work-log-rows">
            {parts.map((row) => {
              const tracking = trackingSummary(row);
              return (
                <li key={row.id}>
                  <div className="work-log-row-main">
                    {row.photo_url ? <WorkLogPhotoThumb path={row.photo_url} /> : null}
                    <div>
                      <strong>{partLabel(row)}</strong>
                      <p>
                        {(CATEGORIES.find((c) => c.id === row.category)?.label || row.category) +
                          (row.install_date ? ` · ${row.install_date}` : '') +
                          (row.warranty_months ? ` · ${row.warranty_months} mo warranty` : '')}
                      </p>
                      {tracking.length > 0 ? (
                        <p className="muted-row">{tracking.join(' · ')}</p>
                      ) : null}
                      {row.notes ? <p className="muted-row">{row.notes}</p> : null}
                    </div>
                  </div>
                  <button type="button" className="ghost" onClick={() => remove(row.id)}>
                    Remove
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
